import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { getEstudo } from "@/lib/estudo";
import { BancaGrid } from "@/components/BancaGrid";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FileText, CheckCircle2, XCircle, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { SIMULADOS } from "@/constants/testIds/vc";

const QTDS = [10, 20, 30, 50];

export default function SimuladosPage() {
  const est = getEstudo();
  const [banca, setBanca] = useState(est.banca || "Todas");
  const [concurso] = useState(est.concurso || "Todas");
  const [disciplina, setDisciplina] = useState("Todas");
  const [disciplinas, setDisciplinas] = useState([]);
  const [quantidade, setQuantidade] = useState(20);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(null);
  const [answers, setAnswers] = useState({});
  const [finished, setFinished] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/questions/filters").then((r) => setDisciplinas(r.data.disciplinas || [])).catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/simulados/generate", { quantidade: Number(quantidade), banca, concurso, disciplina });
      if (!data.simulado.questions.length) { toast.error("Sem questões para esta seleção."); return; }
      setRunning(data.simulado); setAnswers({}); setFinished(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const score = running ? running.questions.filter((q) => answers[q.id] === q.correct).length : 0;

  const finish = async () => {
    setFinished(true);
    setSaving(true);
    try {
      const payload = running.questions.filter((q) => answers[q.id]).map((q) => ({ question_id: q.id, selected: answers[q.id] }));
      await api.post("/simulados/submit", { answers: payload });
      toast.success("Desempenho registrado (alimenta erros e plano adaptativo).");
    } catch (e) {
      toast.error("Não foi possível salvar o desempenho.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={SIMULADOS.page} className="mx-auto max-w-3xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
      <div>
        <h1 className="text-2xl font-extrabold text-white">Simulados</h1>
        <p className="text-sm text-slate-400">Monte um simulado real com questões CEBRASPE e CESGRANRIO.</p>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 space-y-4">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Banca</p>
          <BancaGrid value={banca} onSelect={setBanca} />
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
          <select value={disciplina} onChange={(e) => setDisciplina(e.target.value)} data-testid="simulados-disciplina"
            className="col-span-2 w-full min-w-0 max-w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white sm:col-span-1">
            <option value="Todas">Todas as disciplinas</option>{disciplinas.map((d) => <option key={d}>{d}</option>)}
          </select>
          <select value={quantidade} onChange={(e) => setQuantidade(e.target.value)} data-testid="simulados-quantidade"
            className="w-full min-w-0 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white">
            {QTDS.map((n) => <option key={n} value={n}>{n} questões</option>)}
          </select>
          <button onClick={generate} disabled={generating} data-testid="simulados-generate-btn"
            className="flex min-w-0 items-center justify-center gap-2 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-60">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Gerar simulado
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-xs text-slate-500">
        <FileText className="h-4 w-4" /> O desempenho do simulado alimenta seu Caderno de Erros e o Plano Adaptativo.
      </div>

      <Dialog open={!!running} onOpenChange={(o) => !o && setRunning(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-700 bg-slate-900 text-white sm:max-w-2xl">
          {running && (
            <div className="space-y-4">
              <DialogTitle className="text-lg font-extrabold">{running.nome} · {running.num_questoes} questões</DialogTitle>
              {running.questions.map((q, i) => (
                <div key={q.id} className="rounded-xl border border-slate-800 bg-slate-800/40 p-4">
                  <p className="text-sm text-slate-100"><strong>{i + 1}.</strong> {q.enunciado}</p>
                  <div className="mt-2 space-y-1.5">
                    {q.options.map((o) => {
                      const sel = answers[q.id] === o.key;
                      const showCorrect = finished && o.key === q.correct;
                      const showWrong = finished && sel && o.key !== q.correct;
                      return (
                        <button key={o.key} disabled={finished} data-testid={`sim-${q.id}-opt-${o.key}`}
                          onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.key }))}
                          className={`flex w-full gap-2 rounded-lg border p-2 text-left text-xs transition-colors ${
                            showCorrect ? "border-emerald-500 bg-emerald-500/10"
                            : showWrong ? "border-red-500 bg-red-500/10"
                            : sel ? "border-emerald-500 bg-emerald-500/5 text-white"
                            : "border-slate-700 text-slate-300 hover:border-slate-600"}`}>
                          <span className="font-bold">{o.key}</span> {o.text}
                          {showCorrect && <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-400" />}
                          {showWrong && <XCircle className="ml-auto h-4 w-4 text-red-400" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {!finished ? (
                <button data-testid="sim-finish-button" onClick={finish}
                  className="w-full rounded-xl bg-emerald-500 py-3 text-sm font-bold text-white hover:bg-emerald-600 active:scale-95">
                  Finalizar simulado
                </button>
              ) : (
                <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 text-center">
                  <p className="font-mono-stats text-3xl font-bold text-white">{score}/{running.questions.length}</p>
                  <p className="text-sm text-emerald-400">Aproveitamento: {Math.round((score / running.questions.length) * 100)}%</p>
                  {saving && <p className="mt-1 text-xs text-slate-400">Salvando desempenho…</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
